<template>
  <section
    aria-label="Interactive campus map"
    class="r-cover transition"
    :class="{
      'lg:pl-144': !isTour && categoriesOpen && !isLocation,
      'lg:pl-78': !isTour && isLocation
    }"
  >
    <div
      id="map"
      ref="Map"
      class="rv-map__leaflet h-full w-full z-0"
      aria-label="Map with location"
    />

    <div
      v-if="showLatLngDisplay"
      class="absolute bottom-56 right-6 p-3 bg-white text-sm"
    >
      <span class="block">Latitude: {{ Math.round(dblClickCoords.x) }}</span>
      <span class="block">Longitude: {{ Math.round(dblClickCoords.y) }}</span>
    </div>

    <BtnsExtra v-if="!showTourMap" />

    <BtnsMap
      v-if="!showTourMap"
      class="absolute bottom-16 right-3 md:right-6 lg:bottom-6"
      :current-zoom="currentZoom"
      @mapCenter="mapCenter"
      @mapZoomIn="mapZoomIn"
      @mapZoomOut="mapZoomOut"
    />
  </section>
</template>

<script>
/* global L: false */
import { mapState, mapGetters } from 'vuex'
import BtnsMap from '@/components/buttons/BtnsMap.vue'
import BtnsExtra from '@/components/buttons/BtnsExtra.vue'

import 'leaflet/dist/leaflet.css'
import 'leaflet/dist/leaflet.js'
import 'leaflet-rastercoords'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster/dist/leaflet.markercluster.js'

export default {
  name: 'Map',

  components: {
    BtnsMap,
    BtnsExtra
  },

  props: {
    category: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      publicPath: process.env.BASE_URL,
      map: '',
      img: '',
      landmarkLayer: '',
      locationLayer: '',
      dblClickCoords: '',
      showLatLngDisplay: false,
      currentZoom: 3
    }
  },

  computed: {
    ...mapState([
      'mobile',
      'phone',
      'touch',
      'showTourMap',
      'isTour',
      'categoriesOpen',
      'isLocation'
    ]),
    ...mapGetters(['level', 'post']),

    mapMarkers() {
      if (this.category) {
        const locations = this.category.children.filter(
          ({ type }) => type === 'location'
        )

        return locations
      }
      return []
    }
  },

  watch: {
    'category.slug': {
      immediate: true,
      handler() {
        setTimeout(() => {
          if (this.category) {
            this.addMapMarkers()
          }
        }, 400)
      }
    },

    post: {
      immediate: true,
      handler() {
        if (!this.category) {
          setTimeout(() => {
            this.addTourMarker()
          }, 400)
        }
      }
    },

    categoriesOpen: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    },

    isLocation: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    }
  },

  mounted() {
    this.initMap()

    document.onkeyup = e => {
      if (e.ctrlKey && e.altKey && e.shiftKey && e.which === 85) {
        this.showLatLngDisplay = !this.showLatLngDisplay
      }
    }
  },

  methods: {
    initMap() {
      // Original image dimensions
      this.img = [15360, 11635]

      const minZoom = this.phone ? 2 : 2
      const maxZoom = this.phone ? 4 : 6
      const tiles = 'tiles_15360x11635'

      // Create the map
      this.map = L.map('map', {
        minZoom: minZoom,
        maxZoom: maxZoom,
        zoomControl: false,
        layerControl: false
      })

      // Assign map and image dimensions
      this.rc = new L.RasterCoords(this.map, this.img)
      // Set max zoom Level (might be `x` if gdal2tiles was called with `-z 0-x` option)
      // this.map.setMaxZoom(this.rc.zoomLevel(6))
      // Set the view in the middle of the image
      this.map.setView(this.rc.unproject([this.img[0] / 2, this.img[1] / 2]), 3)
      // Define layer group for markers
      this.layerGroup = L.featureGroup([])
      // The tile layer containing the image generated with gdal2tiles --leaflet ...
      L.tileLayer(`${this.publicPath}uploads/${tiles}/{z}/{x}/{y}.png`, {
        noWrap: true
      }).addTo(this.map)

      // Just the utility to get coords
      this.map.on('dblclick', event => {
        this.dblClickCoords = this.rc.project(event.latlng)
      })
      // Beacuse the popup doesn't exist until clicked
      this.map.on('popupopen', this.bindPopupClick)
      this.map.on('popupclose', this.unbindPopupClick)

      this.map.on('zoomend', () => {
        this.currentZoom = this.map.getZoom()
      })
    },

    mapCenter() {
      this.map.setView(
        this.rc.unproject([this.img[0] / 2, this.img[1] / 2]),
        3,
        {
          animate: true,
          pan: { duration: 1 }
        }
      )
    },

    mapZoomIn() {
      this.map.zoomIn()
    },

    mapZoomOut() {
      this.map.zoomOut()
    },

    addTourMarker() {
      if (this.locationLayer) {
        this.map.removeLayer(this.locationLayer)
      }

      this.locationLayer = L.featureGroup([]).addTo(this.map)

      const marker = L.marker(
        this.rc.unproject([
          this.post.coordinates.lat,
          this.post.coordinates.lng
        ]),
        {
          icon: L.icon({
            iconUrl: require(`@/assets/icon-marker.svg`),
            iconSize: [50, 50]
          }),
          keyboard: false
        }
      ).addTo(this.locationLayer)

      const latLng = this.rc.project(marker.getLatLng())

      this.map.setView(this.rc.unproject(latLng), this.map.getZoom(), {
        animate: true,
        pan: { duration: 1 }
      })
    },

    addMapMarkers() {
      if (this.locationLayer) {
        this.map.removeLayer(this.locationLayer)
      }

      this.locationLayer = L.markerClusterGroup({
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        maxClusterRadius: 80,
        spiderfyDistanceMultiplier: 2,
        removeOutsideVisibleBounds: false,
        spiderLegPolylineOptions: {
          opacity: 0
        },
        zoomToBoundsOnClick: true
      }).addTo(this.map)

      this.mapMarkers.map(item => {
        const img = {
          src: item?.thumbnail?.sizes?.thumbnail,
          alt: item?.thumbnail?.alt
        }

        const popupMarkup = `
            <div
              class="block min-h-24 w-72 cursor-pointer flex bg-primary"
              data-slug="${item.slug}"
            >
              <div
                class="w-1/3 bg-cover bg-center flex flex-col flex-grow pointer-events-none flex-none"
                style="background-image: url(${img.src})"
              ></div>
              <div class="p-3 flex justify-between flex-col flex-grow pointer-events-none">
                <span class="block mb-2 text-base text-white pointer-events-none">
                  ${this.htmlDecode(item.title)}
                </span>

                <span class="text-sm text-white pointer-events-none">
                  <span>Take a look around</span>
                  <img src="${require('@/assets/icon-arrow.svg')}" class="h-3 w-auto inline-block ml-1" />
                </span>
              </div>
            </div>
      `

        const popup = new L.Popup({
          closeButton: false,
          offset: [0, -10]
        }).setContent(popupMarkup)

        const marker = L.marker(
          this.rc.unproject([item.coordinates.lat, item.coordinates.lng]),
          {
            icon: L.icon({
              iconUrl: require(`@/assets/icon-marker.svg`),
              iconSize: [50, 50]
            }),
            keyboard: false
          }
        )
          .bindPopup(popup)
          .addTo(this.locationLayer)

        const latLng = this.rc.project(marker.getLatLng())
        const offsetLatLng = { x: latLng.x, y: latLng.y }

        L.DomEvent.addListener(marker, 'click', () => {
          this.map.setView(this.rc.unproject(offsetLatLng), 5, {
            animate: true,
            pan: { duration: 1 }
          })
        })

        const markerListLink = document.querySelector(
          `.map__marker-link--${item.slug}`
        )

        const markerListItem = L.DomUtil.create(
          'div',
          'map__marker-link',
          markerListLink
        )

        L.DomEvent.addListener(markerListItem, 'click', () => {
          if (!marker._icon) {
            marker.__parent.spiderfy()
          }

          marker.openPopup()

          this.map.setView(
            this.rc.unproject(offsetLatLng),
            this.map.getZoom(),
            {
              animate: true,
              pan: { duration: 1 }
            }
          )
        })

        // marker.on('popupopen', () => {})
        // marker.on('popupclose', () => {})
        // L.DomEvent.addListener(markerListItem, 'mouseover', () => {})
        // L.DomEvent.addListener(markerListItem, 'mouseout', () => {})

        // The marker domNode for handling focus outside of Leaflets built in methods
        //   const markerDomNode = marker.getElement()
        //
        //   let mouseDown = false
        //
        //   markerDomNode.addEventListener('mousedown', () => {
        //     mouseDown = true
        //   })
        //
        //   markerDomNode.addEventListener('mouseup', () => {
        //     mouseDown = false
        //   })
        //
        //   // Handle marker focus events
        //   markerDomNode.addEventListener('focus', e => {
        //     if (!this.touch && !mouseDown) {
        //       marker.openPopup()
        //
        //       this.map.setView(
        //         this.rc.unproject(offsetLatLng),
        //         this.map.getZoom(),
        //         { animate: true, pan: { duration: 1 } }
        //       )
        //     }
        //     this.$announcer.set(this.htmlDecode(`Map location ${item.title}`))
        //
        //     e.target.addEventListener('keyup', () => {
        //       if (event.keyCode === 27) {
        //         marker.closePopup()
        //       }
        //     })
        //
        //     e.target.addEventListener('keyup', () => {
        //       if (event.keyCode === 13) {
        //         this.handleNavigation(item)
        //       }
        //     })
        //   })
      })

      // this.mapCenter()
      if (this.mapMarkers.length) {
        this.map.fitBounds(
          this.locationLayer.getBounds(),
          { animate: true, duration: 0.5 },
          { padding: [50, 50] }
        )
      }
    },

    fitMarkerBounds() {},

    handleNavigation(item) {
      this.$router
        .push({
          name: 'location-media',
          params: {
            level_id: this.level,
            category_id: this.category.slug,
            location_id: item.slug,
            media_id: item?.children[0]?.slug
          }
        })
        .catch(() => {})
    },

    bindPopupClick(e) {
      if (e.popup) {
        e.popup._wrapper.addEventListener('click', this.handlePopupClick)
      }
    },

    unbindPopupClick(e) {
      if (e.popup) {
        e.popup._wrapper.removeEventListener('click', this.handlePopupClick)
      }
    },

    handlePopupClick(e) {
      const location = this.mapMarkers.find(
        ({ slug }) => slug === e.target.dataset.slug
      )

      this.handleNavigation(location)
    }
  }
}
</script>

<style>
#no-touch .rv-map__leaflet:focus:after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  pointer-events: none;
  user-select: none;
  border: 3px solid var(--color-focus);
  z-index: 500;
}

.leaflet-container {
  /* background-color:rgba(187,226,207,1); */
  background-color: #fff;
}

.leaflet-control-attribution.leaflet-control {
  display: none;
}

.leaflet-marker-icon: {
  border-radius: 1000px;
}

.leaflet-marker-icon:focus {
  outline: none;
  /* border: 3px solid var(--color-focus); */
}

.leaflet-popup-tip {
  background: var(--color-primary);
}

.leaflet-popup-content {
  /* background: transparent; */
  margin: 0;
}

.leaflet-popup-content-wrapper {
  background: transparent;
  border-radius: 0;
  padding: 0;
}

.map__marker-link {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.marker-cluster {
  background-color: rgba(212, 0, 114, 0.5);
}

.marker-cluster div {
  background-color: rgba(212, 0, 114, 1);
}

.marker-cluster span {
  color: white;
}
</style>
